import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { bilingualLines } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  adminPicksApi,
  type AdminPickRow,
  type AdminSubmission,
} from '../../services/adminPicksApi';

type Tab = 'PENDING' | 'APPROVED' | 'MERGED' | 'REJECTED';
const TABS: Tab[] = ['PENDING', 'APPROVED', 'MERGED', 'REJECTED'];

/**
 * /admin/picks/submissions (PRD §5.8): parent suggestions. Approve (after editing
 * if needed), merge into an existing published Pick, or reject with a reason the
 * parent sees in My Home.
 */
export default function AdminPickSubmissions() {
  const siteLanguage = useSiteLanguage();
  const [tab, setTab] = useState<Tab>('PENDING');
  const [items, setItems] = useState<AdminSubmission[]>([]);
  const [published, setPublished] = useState<AdminPickRow[]>([]);
  const [mergeTarget, setMergeTarget] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subs, pubs] = await Promise.all([
        adminPicksApi.submissions(tab),
        tab === 'PENDING' ? adminPicksApi.list({ status: 'PUBLISHED' }) : Promise.resolve([]),
      ]);
      setItems(subs);
      setPublished(pubs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const title = (p: { titleCN: string | null; titleEN: string | null; providerName?: string }) =>
    bilingualLines(p.titleCN, p.titleEN, siteLanguage).filter(Boolean).join(' / ') ||
    p.providerName ||
    '';

  return (
    <AdminLayout
      title="YOYO Picks submissions"
      headerRight={
        <Link
          to="/admin/picks"
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
        >
          All picks
        </Link>
      }
    >
      <div className="mb-4 flex gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium capitalize ${
              tab === t ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.toLowerCase()}
          </button>
        ))}
      </div>
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">No submissions.</p>
      ) : (
        <div className="space-y-4">
          {items.map((s) => {
            const reason = s.reviews.find((r) => r.userId === s.submittedBy?.id);
            return (
              <div key={s.id} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/admin/picks/${s.id}`}
                      className="font-semibold text-gray-900 hover:text-primary"
                    >
                      {title(s)}
                    </Link>
                    <p className="text-xs text-gray-500">
                      {s.providerName} · {s.type === 'EVENT' ? 'Event' : 'Provider'} ·{' '}
                      <a
                        href={s.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {s.externalUrl}
                      </a>
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      By {s.submittedBy?.name || '(no name)'} · {s.submittedBy?.email} ·{' '}
                      {new Date(s.createdAt).toLocaleString('en-US')}
                    </p>
                  </div>
                </div>
                {reason && (
                  <p className="mt-3 whitespace-pre-wrap text-sm text-gray-800">
                    {reason.visited ? '[Visited] ' : ''}
                    {reason.content}
                  </p>
                )}
                {s.submitterNote && (
                  <p className="mt-2 text-xs text-gray-500">Note to the team: {s.submitterNote}</p>
                )}
                {s.rejectionReason && (
                  <p className="mt-2 text-xs text-red-700">Reason: {s.rejectionReason}</p>
                )}
                {tab === 'PENDING' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    <Link
                      to={`/admin/picks/${s.id}`}
                      className="rounded border border-gray-300 px-3 py-1 text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </Link>
                    <button
                      disabled={busyId === s.id}
                      onClick={() => void run(s.id, () => adminPicksApi.approve(s.id))}
                      className="rounded border border-green-600 px-3 py-1 text-green-700 hover:bg-green-50 disabled:opacity-40"
                    >
                      Approve and publish
                    </button>
                    <select
                      value={mergeTarget[s.id] ?? ''}
                      onChange={(e) => setMergeTarget((m) => ({ ...m, [s.id]: e.target.value }))}
                      className="max-w-[260px] rounded border border-gray-300 px-2 py-1"
                    >
                      <option value="">Merge into...</option>
                      {published.map((p) => (
                        <option key={p.id} value={p.id}>
                          {title(p)}
                        </option>
                      ))}
                    </select>
                    <button
                      disabled={busyId === s.id || !mergeTarget[s.id]}
                      onClick={() =>
                        void run(s.id, () => adminPicksApi.merge(s.id, mergeTarget[s.id]))
                      }
                      className="rounded border border-gray-400 px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      Merge
                    </button>
                    <button
                      disabled={busyId === s.id}
                      onClick={() => {
                        const why = window.prompt('Reason (shown to the parent in My Home):');
                        if (!why?.trim()) return;
                        void run(s.id, () => adminPicksApi.reject(s.id, why.trim()));
                      }}
                      className="rounded border border-red-500 px-3 py-1 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
